import { formatEther, parseEther } from 'viem';
import {
  Address,
  FeeTickSpacing,
  LiquidityPool,
  MAX_PRICE,
  MAX_TICK,
  MIN_PRICE,
  MIN_TICK,
  TICK_PRICE_BASE,
  WETH_TOKEN,
  WeiPerEther,
  Zero,
  addressEqual,
  getChainConstant,
} from 'nftx.js';

export function isAddressToken0Weth(
  address: Address | undefined | null,
  wethAddress: Address | undefined | null
) {
  if (!address || !wethAddress) {
    return false;
  }
  return BigInt(address) < BigInt(wethAddress);
}

export function getWethAddress(network: number) {
  return getChainConstant(WETH_TOKEN, network);
}

export function isAddressWeth(
  address: Address | string | undefined | null,
  network: number
) {
  if (!address) {
    return false;
  }
  const wethAddress = getWethAddress(network);
  return addressEqual(address, wethAddress);
}

export function isAddressToken0(
  address: Address | undefined | null,
  network: number
) {
  if (!address) {
    return false;
  }
  const wethAddress = getChainConstant(WETH_TOKEN, network);
  return isAddressToken0Weth(address, wethAddress);
}

export function roundTickToSpacing(tick: number, tickSpacing: FeeTickSpacing) {
  return Math.floor(tick / tickSpacing) * tickSpacing;
}

// takes a tick and calculates price (within allowable tick range)
export function calculatePriceFromTick(
  tick: number | string | bigint,
  isVToken0: boolean
): bigint {
  const tickNum = Number(tick);

  if (tickNum <= MIN_TICK) {
    return isVToken0 ? MIN_PRICE : MAX_PRICE;
  } else if (tickNum >= MAX_TICK) {
    return isVToken0 ? MAX_PRICE : MIN_PRICE;
  } else {
    let price = Math.pow(TICK_PRICE_BASE, tickNum);
    if (!isVToken0) {
      price = 1 / price;
    }
    return parseEther(price.toFixed(18));
  }
}

// takes a price and calculates tick (within allowable price range)
export function calculateTickFromPrice(
  price: bigint,
  tickSpacing: FeeTickSpacing,
  isVToken0: boolean
): number {
  let tick: number;

  if (price <= MIN_PRICE) {
    tick = isVToken0 ? MIN_TICK : MAX_TICK;
  } else if (price >= MAX_PRICE) {
    tick = isVToken0 ? MAX_TICK : MIN_TICK;
  } else {
    // We use floor to match Uniswap's Solidity implementation
    tick = Math.floor(
      Math.log(Number(price) / 1e18) / Math.log(TICK_PRICE_BASE)
    );
    if (!isVToken0) {
      tick = -tick;
    }
  }

  // Ensure tick is within the allowed range
  tick = Math.max(MIN_TICK, Math.min(MAX_TICK, tick));

  // Adjust for tick spacing
  tick = roundTickToSpacing(tick, tickSpacing);

  return tick;
}

// applies a percentage discount or surplus to a tick value
export function applyPercentageToTick(
  tickValue: string,
  tickSpacing: FeeTickSpacing,
  percentage: number,
  direction: 'positive' | 'negative',
  isVToken0: boolean
): number {
  const tick = parseFloat(tickValue);
  let price = calculatePriceFromTick(tick, isVToken0);

  // Calculate the new price
  let newPrice =
    direction === 'positive'
      ? BigInt((price * BigInt(100 + percentage)) / BigInt(100))
      : BigInt((price * BigInt(100 - percentage)) / BigInt(100));

  // Convert price back to tick
  return calculateTickFromPrice(newPrice, tickSpacing, isVToken0);
}

const formatTick = (tick: bigint) => {
  return Number(formatEther(tick ?? Zero));
};

export const getWethAddressFromPool = (pool: LiquidityPool) => {
  return pool.tokens.find((x) => x.symbol.toLowerCase() === 'weth')?.id;
};

export const normalizeTicks = (
  tokenAddress: Address,
  lowerTick: bigint,
  upperTick: bigint,
  pool: LiquidityPool
): [number, number] => {
  const wethAddress = getWethAddressFromPool(pool);
  const lowerTickValue = (lowerTick * WeiPerEther) / WeiPerEther;
  const upperTickValue = (upperTick * WeiPerEther) / WeiPerEther;

  if (!wethAddress)
    return [formatTick(lowerTickValue), formatTick(upperTickValue)];

  const isVToken0 = isAddressToken0Weth(tokenAddress, wethAddress);

  if (!isVToken0 && lowerTickValue !== Zero && upperTickValue !== Zero) {
    return [1 / formatTick(upperTickValue), 1 / formatTick(lowerTickValue)];
  }

  return [formatTick(lowerTickValue), formatTick(upperTickValue)];
};
